/**
 * 功能页「可登记部件」原语（T07）。
 * -----------------------------------------------------------------------------
 * T04 的 Button/IconButton 是固定尺寸的示例件；角色卡页需要更多形态
 * （卡片、文本、标题、输入框、多行输入、开关、动效容器）。这里复用同一套机制：
 *
 *   同一份 `layout` 既用于内联定位、又通过 `useComponentMeta()` 登记进组件清单，
 *   保证「页面上看到的部件」与清单坐标一一对应（安卓移植硬要求）。
 *
 * 坐标约定（单一事实来源）：
 *   - `layout` 一律是**页面局部坐标**（y = 0 是内容区顶部，即画布 y = 24）；
 *     登记时统一加 24dp 状态栏安全区偏移，得到 390x844 画布下的绝对逻辑坐标；
 *   - 渲染时由 `FramedOriginContext` 提供父容器在页面局部坐标下的原点，
 *     子部件按 `layout - 父原点` 相对定位 —— 因此滚动容器 / 卡片嵌套不会算错坐标。
 * ============================================================================= */

import type { ChangeEvent, CSSProperties, ReactElement, ReactNode } from 'react';
import { motion } from 'framer-motion';
import type { Variants } from 'framer-motion';

import { useMotionPresets } from '../../design/motion';
import type { ComponentMeta } from '../../core/types';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { ComponentMetaInput } from '../../ui';
import { CONTENT_TOP } from './layout';
import { FramedOriginContext, useFramedOrigin } from './framedOrigin';

export interface FramedProps {
  /** 组件元数据（id / page / label / layout / zIndex / props / notes）；layout 为页面局部坐标。 */
  meta: ComponentMetaInput;
  className?: string;
  children?: ReactNode;
  title?: string;
  surface?: 'page' | 'panel' | 'control' | 'overlay';
  style?: CSSProperties;
}

function frameStyle(meta: ComponentMetaInput, origin: { x: number; y: number }, zIndex: number): CSSProperties {
  const { layout } = meta;
  return {
    position: 'absolute',
    left: `${layout.x - origin.x}px`,
    top: `${layout.y - origin.y}px`,
    width: `${layout.width}px`,
    height: `${layout.height}px`,
    zIndex,
  };
}

/** 页面局部坐标 → 390x844 画布绝对逻辑坐标（清单登记用）。 */
function toCanvasMeta(
  meta: ComponentMetaInput,
  fallbackRole: ComponentMeta['role'],
): ComponentMeta {
  const { layout } = meta;
  return buildComponentMeta({ ...meta, layout: { ...layout, y: layout.y + CONTENT_TOP } }, fallbackRole);
}

interface FramedPart {
  origin: { x: number; y: number };
  /** 本部件在页面局部坐标下的原点，供子部件解析坐标。 */
  pageOrigin: { x: number; y: number };
  componentMeta: ComponentMeta;
  style: CSSProperties;
}

/** 所有原语共用的登记 + 坐标换算。 */
function useFramedPart(meta: ComponentMetaInput, fallbackRole: ComponentMeta['role']): FramedPart {
  const origin = useFramedOrigin();
  const componentMeta = toCanvasMeta(meta, fallbackRole);
  const pageOrigin = { x: meta.layout.x, y: meta.layout.y };
  return {
    origin,
    pageOrigin,
    componentMeta,
    style: frameStyle(meta, origin, componentMeta.zIndex),
  };
}

/** 背景 / 卡片 / 容器。 */
export function FramedBox({ meta, className, children, surface, style: customStyle }: FramedProps): ReactElement {
  const { pageOrigin, componentMeta, style } = useFramedPart(meta, 'container');
  const ref = useComponentMeta<HTMLDivElement>(componentMeta);

  return (
    <div
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      data-skin-surface={surface}
      style={{ ...style, ...customStyle }}
    >
      <FramedOriginContext.Provider value={pageOrigin}>{children}</FramedOriginContext.Provider>
    </div>
  );
}

/** 文本（p）。 */
export function FramedText({ meta, className, children, title }: FramedProps): ReactElement {
  const { componentMeta, style } = useFramedPart(meta, 'text');
  const ref = useComponentMeta<HTMLParagraphElement>(componentMeta);

  return (
    <p
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      {...(title === undefined ? {} : { title, 'aria-label': title })}
      style={style}
    >
      {children}
    </p>
  );
}

export interface FramedHeadingProps extends FramedProps {
  /** 标题层级：1 = 页面主标题，2 = 区块标题。 */
  level?: 1 | 2;
}

/** 标题（h1 / h2，供无障碍与自动化测试定位页面）。 */
export function FramedHeading({ meta, className, children, level = 2 }: FramedHeadingProps): ReactElement {
  const { pageOrigin, componentMeta, style } = useFramedPart(meta, 'text');
  const ref = useComponentMeta<HTMLHeadingElement>(componentMeta);
  const shared = {
    ref,
    className,
    'data-ui-id': componentMeta.id,
    'data-ui-role': componentMeta.role,
    style,
  };

  return (
    <FramedOriginContext.Provider value={pageOrigin}>
      {level === 1 ? <h1 {...shared}>{children}</h1> : <h2 {...shared}>{children}</h2>}
    </FramedOriginContext.Provider>
  );
}

export interface FramedButtonProps extends FramedProps {
  onClick?: () => void;
  disabled?: boolean;
}

/** 按压带物理回弹的按钮（动效引用 motion.ts 的 tap）。 */
export function FramedButton({
  meta,
  className,
  children,
  onClick,
  disabled,
}: FramedButtonProps): ReactElement {
  const motionPresets = useMotionPresets();
  const { pageOrigin, componentMeta, style } = useFramedPart(meta, 'button');
  const ref = useComponentMeta<HTMLButtonElement>(componentMeta);

  return (
    <motion.button
      ref={ref}
      type="button"
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      aria-label={componentMeta.label}
      disabled={disabled}
      whileTap={motionPresets.tap.whileTap}
      transition={motionPresets.tap.transition}
      style={style}
      onClick={onClick}
    >
      <FramedOriginContext.Provider value={pageOrigin}>{children}</FramedOriginContext.Provider>
    </motion.button>
  );
}

export interface FramedInputProps extends FramedProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'number' | 'time';
  min?: number;
  disabled?: boolean;
  list?: string;
}

/** 单行输入框。 */
export function FramedInput({
  meta,
  className,
  value,
  onValueChange,
  placeholder,
  type = 'text',
  min,
  disabled,
  list,
}: FramedInputProps): ReactElement {
  const { componentMeta, style } = useFramedPart(meta, 'input');
  const ref = useComponentMeta<HTMLInputElement>(componentMeta);

  return (
    <input
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      aria-label={componentMeta.label}
      type={type}
      min={min}
      disabled={disabled}
      list={list}
      placeholder={placeholder}
      value={value}
      onChange={(event) => {
        onValueChange(event.target.value);
      }}
      style={style}
    />
  );
}

/** 多行输入框。 */
export function FramedTextarea({
  meta,
  className,
  value,
  onValueChange,
  placeholder,
}: FramedInputProps): ReactElement {
  const { componentMeta, style } = useFramedPart(meta, 'input');
  const ref = useComponentMeta<HTMLTextAreaElement>(componentMeta);

  return (
    <textarea
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      aria-label={componentMeta.label}
      placeholder={placeholder}
      value={value}
      onChange={(event) => {
        onValueChange(event.target.value);
      }}
      style={style}
    />
  );
}

export interface FramedSwitchProps extends FramedProps {
  checked: boolean;
  onToggle: (next: boolean) => void;
  disabled?: boolean;
  /** Keep the control focusable/clickable so it can explain why it is locked. */
  ariaDisabled?: boolean;
}

export interface FramedSelectProps extends FramedProps {
  value: string;
  onValueChange: (value: string, event: ChangeEvent<HTMLSelectElement>) => void;
  disabled?: boolean;
  children?: ReactNode;
}

/** Registered native select for compact editor rows. */
export function FramedSelect({
  meta,
  className,
  value,
  onValueChange,
  disabled,
  children,
}: FramedSelectProps): ReactElement {
  const { componentMeta, style } = useFramedPart(meta, 'input');
  const ref = useComponentMeta<HTMLSelectElement>(componentMeta);
  return (
    <select
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      aria-label={componentMeta.label}
      value={value}
      disabled={disabled}
      style={style}
      onChange={(event) => onValueChange(event.currentTarget.value, event)}
    >
      {children}
    </select>
  );
}

/** 开关（世界书条目的启用状态）。 */
export function FramedSwitch({
  meta,
  className,
  checked,
  onToggle,
  disabled,
  ariaDisabled,
  children,
}: FramedSwitchProps): ReactElement {
  const motionPresets = useMotionPresets();
  const { pageOrigin, componentMeta, style } = useFramedPart(meta, 'button');
  const ref = useComponentMeta<HTMLButtonElement>(componentMeta);

  return (
    <motion.button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-disabled={ariaDisabled === true ? 'true' : undefined}
      aria-label={componentMeta.label}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      disabled={disabled}
      whileTap={motionPresets.tap.whileTap}
      transition={motionPresets.tap.transition}
      style={style}
      onClick={() => {
        onToggle(!checked);
      }}
    >
      <FramedOriginContext.Provider value={pageOrigin}>{children}</FramedOriginContext.Provider>
    </motion.button>
  );
}

export interface FramedMotionBoxProps extends FramedProps {
  /** 列表入场 / 页面切换动效（引用 motion.ts 的 variants）。 */
  variants?: Variants;
  initial?: string;
  animate?: string;
  exit?: string;
  onClick?: () => void;
}

/**
 * 带动效的容器 / 列表项：与 FramedBox 同源登记，额外承载 variants。
 * 列表入场用 listStagger（容器控制 stagger，子项从下方 12px 带惯性上浮）。
 */
export function FramedMotionBox({
  meta,
  className,
  children,
  variants,
  initial,
  animate,
  exit,
  onClick,
  surface,
}: FramedMotionBoxProps): ReactElement {
  const { pageOrigin, componentMeta, style } = useFramedPart(meta, 'container');
  const ref = useComponentMeta<HTMLDivElement>(componentMeta);

  return (
    <motion.div
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      data-skin-surface={surface}
      variants={variants}
      initial={initial}
      animate={animate}
      exit={exit}
      style={style}
      onClick={onClick}
    >
      <FramedOriginContext.Provider value={pageOrigin}>{children}</FramedOriginContext.Provider>
    </motion.div>
  );
}
